const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), details, [contenteditable="true"]';

export class FocusTrap {
  private _element: HTMLElement;
  private _isActive = false;
  private _direction: 'forward' | 'backward' = 'forward';
  private _handleFocusin: (event: FocusEvent) => void;
  private _handleKeydown: (event: KeyboardEvent) => void;

  constructor(element: HTMLElement) {
    this._element = element;
    this._handleFocusin = this._onFocusin.bind(this);
    this._handleKeydown = this._onKeydown.bind(this);
  }

  activate(): void {
    if (this._isActive) return;

    this._isActive = true;
    document.addEventListener('focusin', this._handleFocusin, true);
    document.addEventListener('keydown', this._handleKeydown, true);

    const focusableChildren = this._getFocusableChildren();
    if (focusableChildren.length > 0) {
      focusableChildren[0].focus();
    } else {
      this._element.focus();
    }
  }

  deactivate(): void {
    if (!this._isActive) return;

    this._isActive = false;
    document.removeEventListener('focusin', this._handleFocusin, true);
    document.removeEventListener('keydown', this._handleKeydown, true);
  }

  private _onFocusin(event: FocusEvent): void {
    if (!this._isActive) return;

    const target = event.target as HTMLElement;
    if (!this._element.contains(target)) {
      const focusableChildren = this._getFocusableChildren();
      if (focusableChildren.length === 0) {
        this._element.focus();
      } else if (this._direction === 'backward') {
        focusableChildren[focusableChildren.length - 1].focus();
      } else {
        focusableChildren[0].focus();
      }
    }
  }

  private _onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Tab') {
      this._direction = event.shiftKey ? 'backward' : 'forward';
    }
  }

  private _getFocusableChildren(): HTMLElement[] {
    const elements = Array.from(
      this._element.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
    );

    return elements.filter((el) => {
      const tabindex = el.getAttribute('tabindex');
      if (tabindex && parseInt(tabindex, 10) < 0) {
        return false;
      }

      const style = window.getComputedStyle(el);
      return style.display !== 'none' && style.visibility !== 'hidden';
    });
  }
}
